// src/pages/activity-logs/ActivityLogsPage.tsx

import { Fragment, useMemo, useState } from "react";
import { FiSearch } from "react-icons/fi";
import AdminLayout from "@/layouts/AdminLayout";
import ColumnFilter from "@/components/shared/ui/ColumnFilter";
import TablePagination from "@/components/shared/ui/TablePagination";
import Input from "@/components/shared/ui/Input";
import { useAdminActivityLogs } from "@/api/features/activity-logs";
import type {
  ActivityLogListQuery,
  ActivityLogRow,
} from "@/api/features/activity-logs/types";

const ACTIONS = [
  "AUTHENTICATE",
  "SEARCH",
  "FARE_QUOTE",
  "FARE_RULE",
  "SSR",
  "BOOK",
  "TICKET",
  "TICKET_PRECHECK",
  "TICKET_FAILED",
  "WALLET_DEBIT",
  "WALLET_REFUND",
  "GET_BOOKING_DETAILS",
];

function fmtTime(iso: string) {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function StatusChip({ status }: { status: "OK" | "FAIL" }) {
  return (
    <span
      className={
        "inline-flex rounded-full px-2 py-0.5 text-[11px] font-medium " +
        (status === "OK"
          ? "bg-emerald-50 text-emerald-700"
          : "bg-rose-50 text-rose-700")
      }
    >
      {status}
    </span>
  );
}

function JsonBlock({ value }: { value: unknown }) {
  if (value === null || value === undefined) {
    return <div className="text-xs text-gray-400">—</div>;
  }
  return (
    <pre className="max-h-80 overflow-auto rounded-md bg-slate-900 p-3 text-[11px] leading-relaxed text-slate-100">
      {JSON.stringify(value, null, 2)}
    </pre>
  );
}

export default function ActivityLogsPage() {
  const [q, setQ] = useState("");
  const [userId, setUserId] = useState("");
  const [action, setAction] = useState("");
  const [status, setStatus] = useState("");
  const [sessionId, setSessionId] = useState("");
  const [page, setPage] = useState(1);
  const [expanded, setExpanded] = useState<number | null>(null);

  const resetPageAnd = (fn: () => void) => {
    setPage(1);
    fn();
  };

  const query: ActivityLogListQuery = useMemo(
    () => ({
      q: q.trim() || undefined,
      userId: userId.trim() ? Number(userId.trim()) : undefined,
      action: action || undefined,
      status: (status || undefined) as any,
      sessionId: sessionId.trim() || undefined,
      order: "desc",
      page,
      limit: 100,
    }),
    [q, userId, action, status, sessionId, page]
  );

  const { data, isLoading, isError } = useAdminActivityLogs(query);

  const rows: ActivityLogRow[] = data?.data ?? [];
  const total = data?.total ?? 0;
  const pageSize = data?.pageSize ?? 100;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const serialStart = (page - 1) * pageSize;

  return (
    <AdminLayout>
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Flight Activity Logs</h1>
          <p className="mt-1 max-w-2xl text-xs text-gray-500">
            Per-user trail of every TBO step (search → fare-quote → ssr → book →
            ticket) and wallet event, with request/response JSON.
          </p>
        </div>

        <div className="relative">
          <FiSearch className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            value={q}
            onChange={(e) => {
              setPage(1);
              setQ(e.target.value);
            }}
            placeholder="Search activity logs…"
            aria-label="Search activity logs"
            className="h-[38px] w-72 rounded-lg border border-gray-300 bg-white pl-9 pr-3 text-sm outline-none placeholder:text-gray-400 focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20"
          />
        </div>
      </div>

      {/* Table */}
      {/* Square bottom: the pagination bar below completes this card. */}
      <div className="overflow-hidden rounded-t-2xl border border-gray-200 bg-white">
        <table className="w-full text-left text-xs">
          <thead className="bg-gray-50 text-gray-600">
            <tr className="border-b border-gray-200">
              <th className="px-3 py-2 font-medium">S.No</th>
              <th className="px-3 py-2 font-medium">Time</th>
              <th className="px-3 py-2 font-medium">
                <div className="flex items-center gap-1.5">
                  <span>User</span>
                  <ColumnFilter label="User" menuWidth={220}>
                    <Input
                      type="number"
                      placeholder="User ID…"
                      value={userId}
                      onChange={(e) => resetPageAnd(() => setUserId(e.target.value))}
                    />
                  </ColumnFilter>
                </div>
              </th>
              <th className="px-3 py-2 font-medium">
                <div className="flex items-center gap-1.5">
                  <span>Action</span>
                  <ColumnFilter label="Action" menuWidth={220}>
                    <select
                      value={action}
                      onChange={(e) => resetPageAnd(() => setAction(e.target.value))}
                      className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20"
                    >
                      <option value="">All actions</option>
                      {ACTIONS.map((a) => (
                        <option key={a} value={a}>
                          {a}
                        </option>
                      ))}
                    </select>
                  </ColumnFilter>
                </div>
              </th>
              <th className="px-3 py-2 font-medium">
                <div className="flex items-center gap-1.5">
                  <span>Status</span>
                  <ColumnFilter label="Status" menuWidth={200}>
                    <select
                      value={status}
                      onChange={(e) => resetPageAnd(() => setStatus(e.target.value))}
                      className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20"
                    >
                      <option value="">All statuses</option>
                      <option value="OK">OK</option>
                      <option value="FAIL">FAIL</option>
                    </select>
                  </ColumnFilter>
                </div>
              </th>
              <th className="px-3 py-2 font-medium">HTTP</th>
              <th className="px-3 py-2 font-medium">ms</th>
              <th className="px-3 py-2 font-medium">Err</th>
              <th className="px-3 py-2 font-medium">Message</th>
              <th className="px-3 py-2 font-medium">
                <div className="flex items-center gap-1.5">
                  <span>Session</span>
                  <ColumnFilter label="Session" menuWidth={260}>
                    <Input
                      placeholder="Exact session ID…"
                      value={sessionId}
                      onChange={(e) => resetPageAnd(() => setSessionId(e.target.value))}
                    />
                  </ColumnFilter>
                </div>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {isLoading && (
              <tr>
                <td colSpan={10} className="px-3 py-6 text-center text-gray-500">
                  Loading…
                </td>
              </tr>
            )}
            {isError && !isLoading && (
              <tr>
                <td colSpan={10} className="px-3 py-6 text-center text-rose-500">
                  Failed to load activity logs.
                </td>
              </tr>
            )}
            {!isLoading && !isError && rows.length === 0 && (
              <tr>
                <td colSpan={10} className="px-3 py-6 text-center text-gray-500">
                  No activity found for these filters.
                </td>
              </tr>
            )}
            {rows.map((r, i) => (
              <Fragment key={r.id}>
                <tr
                  onClick={() => setExpanded(expanded === r.id ? null : r.id)}
                  className="cursor-pointer hover:bg-gray-50/60"
                >
                  <td className="px-3 py-2 text-gray-500">{serialStart + i + 1}</td>
                  <td className="whitespace-nowrap px-3 py-2 text-gray-600">
                    {fmtTime(r.createdAt)}
                  </td>
                  <td className="px-3 py-2 text-gray-600">{r.userId ?? "—"}</td>
                  <td className="px-3 py-2 font-medium text-gray-900">{r.action}</td>
                  <td className="px-3 py-2">
                    <StatusChip status={r.status} />
                  </td>
                  <td className="px-3 py-2 text-gray-600">{r.httpStatus ?? "—"}</td>
                  <td className="px-3 py-2 text-gray-600">{r.durationMs ?? "—"}</td>
                  <td className="px-3 py-2 text-rose-600">{r.errorCode ?? ""}</td>
                  <td className="max-w-[280px] truncate px-3 py-2 text-gray-700" title={r.message ?? ""}>
                    {r.message ?? ""}
                  </td>
                  <td className="max-w-[140px] truncate px-3 py-2 font-mono text-[10px] text-gray-500" title={r.sessionId ?? ""}>
                    {r.sessionId ?? "—"}
                  </td>
                </tr>
                {expanded === r.id && (
                  <tr className="bg-gray-50/60">
                    <td colSpan={10} className="px-3 py-3">
                      <div className="mb-2 flex flex-wrap gap-x-6 gap-y-1 text-[11px] text-gray-600">
                        <span><b>Trace:</b> {r.traceId ?? "—"}</span>
                        <span><b>Token:</b> {r.tokenId ?? "—"}</span>
                        <span><b>PNR:</b> {r.pnr ?? "—"}</span>
                        <span><b>IP:</b> {r.ip ?? "—"}</span>
                        <span className="max-w-full break-all"><b>ResultIndex:</b> {r.resultIndex ?? "—"}</span>
                      </div>
                      <div className="grid gap-3 md:grid-cols-2">
                        <div>
                          <div className="mb-1 text-[11px] font-semibold text-gray-500">Request</div>
                          <JsonBlock value={r.requestJson} />
                        </div>
                        <div>
                          <div className="mb-1 text-[11px] font-semibold text-gray-500">Response</div>
                          <JsonBlock value={r.responseJson} />
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination — shared card foot, same as the other list screens */}
      <TablePagination
        page={page}
        pageSize={pageSize}
        total={total}
        totalPages={totalPages}
        onPageChange={setPage}
      />

    </AdminLayout>
  );
}
